import { LOCATION_CHANGE } from 'connected-react-router';

import { createMiddleware } from '@shared/redux';
import { match } from '@shared/utils/match';
import { showErrorToast } from '@shared/toasts/toasts.state';
import {
    acceptConfirmForgotPassword,
    CONFIRM_FORGOT_PASSWORD_ACCEPTED,
    CONFIRM_FORGOT_PASSWORD_REJECTED,
    CONFIRM_FORGOT_PASSWORD_SUBMITTED,
    CONFIRM_FORGOT_PASSWORD_VALIDATE_PASSWORD,
    CONFIRM_FORGOT_PASSWORD_VALIDATE_CONFIRM_PASSWORD,
    rejectConfirmForgotPassword,
    handleFieldsErrors,
} from './confirm-forgot-password-state';
import * as AuthService from '../auth-service';
import {
    isSignInFromSignUp, rejectSignIn, setUser,
} from '../sign-in/sign-in.state';
import { ROUTES } from '../auth.constants';
import { validateForgotPassword, validateConfirmPassword } from './confirm-forgot-password.validation';
import { redirectToHome } from '../auth-base-state';
import { isSignInFromInvite } from '../confirm-invite/confirm-invite-state';
import { redirectToSplashScreen } from '../splash-screen/splash-screen-state';
import { getCognitoErrorMessage } from './confirm-forgot-password.utils';
import { CONFIRM_FORGOT_PASSWORD_TEXTS } from './confirm-forgot-password.constants';
import { forgotPassword } from '../auth-service';

const handleResendForgotPasswordLink = async (error, username) => {
    const { CODE_MISMATCH_OR_EXPIRED_CODE_EXCEPTION } = CONFIRM_FORGOT_PASSWORD_TEXTS;

    if (getCognitoErrorMessage(error) === CODE_MISMATCH_OR_EXPIRED_CODE_EXCEPTION) {
        await forgotPassword({ username });
    }
};

export const confirmForgotPasswordMiddleware = createMiddleware({
    [LOCATION_CHANGE]: match((_, action) => action.payload.location.pathname, {
        [ROUTES.CONFIRM_FORGOT_PASSWORD]: () => (
            AuthService.currentAuthenticatedUser()
                .then(() => redirectToHome())
                .catch(() => null)
        ),
    }),

    [CONFIRM_FORGOT_PASSWORD_SUBMITTED]: async (state, action) => {
        const isFromInvite = isSignInFromInvite(state);
        const username = isFromInvite ? state.auth.confirmInvite.username : action.payload.username;

        try {
            await validateForgotPassword(action.payload);
        } catch (error) {
            return rejectConfirmForgotPassword({ code: 'YupException', error });
        }

        try {
            const user = await AuthService.forgotPasswordSubmit({
                username,
                code: action.payload.code,
                password: action.payload.password,
            });

            return acceptConfirmForgotPassword({ user });
        } catch (error) {
            return [
                rejectConfirmForgotPassword({ code: error.code || 'UnknownException', error }),
                showErrorToast({
                    message: getCognitoErrorMessage(error),
                    options: {
                        autoClose: false,
                        onClick: () => (
                            handleResendForgotPasswordLink(error, action.payload.username)
                        ),
                    },
                }),
            ];
        }
    },
    [CONFIRM_FORGOT_PASSWORD_ACCEPTED]: (_, action) => ([
        setUser({ user: action.payload.user }),
        redirectToSplashScreen(),
    ]),
    [CONFIRM_FORGOT_PASSWORD_REJECTED]: (state, action) => (
        rejectSignIn({
            code: action.payload.code,
            error: action.payload.error,
            fromSignUp: isSignInFromSignUp(state),
        })
    ),
    [CONFIRM_FORGOT_PASSWORD_VALIDATE_PASSWORD]: (_, action) => (
        validateForgotPassword(action.payload)
            .then(() => handleFieldsErrors({
                error: { password: null, confirmPassword: null },
            }))
            .catch(({ password, confirmPassword }) => handleFieldsErrors({
                error: {
                    password,
                    confirmPassword: action.payload.confirmPassword
                        ? confirmPassword
                        : null,
                },
            }))
    ),
    [CONFIRM_FORGOT_PASSWORD_VALIDATE_CONFIRM_PASSWORD]: (_, action) => (
        validateConfirmPassword(action.payload)
            .then(() => handleFieldsErrors({
                error: { ...action.payload.error, confirmPassword: null },
            }))
            .catch(({ confirmPassword }) => handleFieldsErrors({
                error: { ...action.payload.error, confirmPassword },
            }))
    ),
});
